import React, { FC } from 'react';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalFooter
} from '@epoch-ui/components/Modal/modal';
import { Button } from '@epoch-ui/components/Button';
import { Icon } from '@epoch-ui/components/Icon';
import LoadingSpinner from 'components/common/loaders/LoadingSpinner';
import AIBetaPill from 'components/common/AIModals/AIBetaPill';

interface EditDescriptionConfirmProps extends React.ComponentProps<typeof Modal> {
  replaceDescription: (description: string) => void;
  appendDescription: (description: string) => void;
  description: string;
  loading: boolean;
}

export const EditDescriptionConfirm: FC<React.PropsWithChildren<EditDescriptionConfirmProps>> = ({
  replaceDescription,
  appendDescription,
  description,
  loading,
  onOpenChange: onRequestClose,
  open: isOpen,
  ...props
}) => {
  const handleClose = (): void => {
    onRequestClose?.(false);
  };

  const handleReplace = (): void => {
    replaceDescription(description);
    handleClose();
  };

  const handleAppend = (): void => {
    appendDescription(description);
    handleClose();
  };

  return (
    <Modal open={isOpen} onOpenChange={(open) => !open && handleClose()} {...props}>
      <ModalContent size="md" className="tw-max-w-[556px]">
        <ModalHeader>
          <div className="tw-flex tw-flex-col tw-items-start">
            <AIBetaPill />
            <ModalTitle>Here&apos;s your new description</ModalTitle>
          </div>
        </ModalHeader>

        <div className="tw-flex tw-h-full tw-min-h-[120px] tw-overflow-y-scroll tw-rounded-lg tw-border-solid tw-border-border-grey tw-p-4">
          {loading ? (
            <div
              data-testid="loading-spinner"
              className="tw-flex tw-flex-1 tw-items-center tw-justify-center"
            >
              <LoadingSpinner />
            </div>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: description }} />
          )}
        </div>

        <div className="tw-mt-3 tw-text-xs tw-font-normal tw-text-primary-grey">
          AI-generated content may not be fully accurate.&nbsp;
          <a
            href="https://help.epochapp.com/articles/ai-tools"
            target="_blank"
            rel="noopener noreferrer"
            className="tw-text-xs tw-font-normal tw-text-primary-grey tw-underline"
          >
            Learn more
          </a>
        </div>

        <ModalFooter>
          <Button variant="secondary" onClick={handleAppend} disabled={!description || loading}>
            <Icon name="plus" size={16} className="tw-mr-2" />
            Add below
          </Button>
          <Button onClick={handleReplace} disabled={!description || loading}>
            <Icon name="arrow-left-right" size={16} className="tw-mr-2" />
            Replace description
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
